import { type ReactNode } from "react";
import { Link } from "react-router-dom";

interface PageHeaderProps {
  back?: { label: string; href: string; onClick?: () => void };
  eyebrow?: string;
  title: string;
  /** Word within the title to render in accent italic. Must appear verbatim in title. */
  emphasis?: string;
  subhead?: string;
  /** Content placed in the right rail beside the H1 (stat strip, audio status, etc.) */
  right?: ReactNode;
}

export function PageHeader({ back, eyebrow, title, emphasis, subhead, right }: PageHeaderProps): ReactNode {
  const displayTitle = title.endsWith(".") || title.endsWith("!") || title.endsWith("?")
    ? title
    : `${title}.`;

  const h1Content = emphasis
    ? renderWithEmphasis(displayTitle, emphasis)
    : displayTitle;

  return (
    <header className="mb-7">
      {back && (
        <Link
          to={back.href}
          onClick={back.onClick}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors mb-4 inline-block"
        >
          {back.label}
        </Link>
      )}
      {eyebrow && (
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">
          {eyebrow}
        </p>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-serif text-4xl font-medium leading-[1.05] text-ink">
            {h1Content}
          </h1>
          {subhead && (
            <p className="font-serif italic text-base text-inkSoft mt-1">
              {subhead}
            </p>
          )}
        </div>
        {right && (
          <div className="shrink-0 flex items-center gap-3 pt-1">
            {right}
          </div>
        )}
      </div>
    </header>
  );
}

function renderWithEmphasis(title: string, emphasis: string): ReactNode {
  const idx = title.toLowerCase().indexOf(emphasis.toLowerCase());
  if (idx === -1) return title;
  const before = title.slice(0, idx);
  const word = title.slice(idx, idx + emphasis.length);
  const after = title.slice(idx + emphasis.length);
  return (
    <>
      {before}
      <em className="not-italic italic text-accent">{word}</em>
      {after}
    </>
  );
}
